import React, { useEffect, useState } from 'react';
import { Card, DatePicker, Input, Button, Space, Typography, Spin, Alert, message } from 'antd';
import { SaveOutlined, LockOutlined } from '@ant-design/icons';
import axiosClient from '@/api/axiosClient';
import dayjs from 'dayjs';
import { useAuth } from '@/auth/useAuth';

const { Text } = Typography;

interface ProposalPlanData {
  id?: string;
  opportunityId?: string;
  kickoffDate?: string;
  submissionDeadline?: string;
  internalDeadline?: string;
  notes?: string;
}

interface KickoffPlanFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const KickoffPlanForm: React.FC<KickoffPlanFormProps> = ({ opportunityId, onComplete }) => {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(['BD_MANAGER', 'ADMIN', 'CEO']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingPlan, setExistingPlan] = useState<ProposalPlanData | null>(null);
  const [kickoffDate, setKickoffDate] = useState<dayjs.Dayjs | null>(null);
  const [notes, setNotes] = useState('');

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/opportunities/${opportunityId}/plan`);
      if (response.data) {
        setExistingPlan(response.data);
        if (response.data.kickoffDate) {
          setKickoffDate(dayjs(response.data.kickoffDate));
        }
        setNotes(response.data.notes || '');
      }
    } catch {
      // no plan yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [opportunityId]);

  const handleSave = async () => {
    if (!kickoffDate) {
      message.warning('Kickoff date is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kickoffDate: kickoffDate.format('YYYY-MM-DD'),
        notes: notes || undefined,
      };
      if (existingPlan?.id) {
        await axiosClient.put(`/opportunities/${opportunityId}/plan/${existingPlan.id}`, payload);
      } else {
        await axiosClient.post(`/opportunities/${opportunityId}/plan`, payload);
      }
      message.success('Kickoff plan saved');
      await fetchPlan();
      onComplete();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save kickoff plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading kickoff plan...
      </Card>
    );
  }

  const isComplete = Boolean(existingPlan?.kickoffDate);

  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 14 }}>Phase C: Kickoff Plan</Text>}
      style={{ marginTop: 12, borderColor: isComplete ? 'var(--color-success)' : 'var(--color-warning)' }}
    >
      {!canEdit && (
        <Alert
          message={<span><LockOutlined /> Read-only — only the BD Manager, Admin, or CEO can edit this section.</span>}
          type="info"
          showIcon={false}
          style={{ marginBottom: 12 }}
        />
      )}
      {!isComplete && (
        <Alert
          message="A kickoff date is required to advance to Phase D"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Kickoff Date <span style={{ color: 'var(--color-error)' }}>*</span>
          </Text>
          <DatePicker
            value={kickoffDate}
            onChange={(date) => setKickoffDate(date)}
            style={{ width: '100%' }}
            placeholder="Select kickoff date"
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Notes</Text>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Kickoff meeting notes, agenda items..."
            rows={3}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!canEdit || !kickoffDate}
          >
            Save Kickoff Plan
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default KickoffPlanForm;
